import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './Header.css';  // Ensure this file contains the required CSS
import { FaSearch } from 'react-icons/fa';  // Import icons
import { useNavigate } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';

const Header = ({ onSearch, user, setUser, setMenuOpen }) => {
  const navigate = useNavigate(); // Initialize the useNavigate hook
  const [query, setQuery] = useState('');
  const [showAuthOptions, setShowAuthOptions] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const authRef = useRef(null);

  const API_BASE = process.env.REACT_APP_API_BASE || (window.location.hostname === 'localhost' ? 'http://localhost:5000' : 'https://studyvarious.com/api');

  // Listen for external requests to open the auth modal
  useEffect(() => {
    const onOpenAuth = () => setShowAuthOptions(true);
    window.addEventListener('open-auth', onOpenAuth);
    return () => window.removeEventListener('open-auth', onOpenAuth);
  }, []);

  // Listen for scroll events to show/hide border
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setIsScrolled(scrollTop > 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSearch = () => {
    navigate('/');
    onSearch(query);  // Call the passed search function
    setMenuOpen(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();  // Trigger search on Enter key press
    }
  };

  const handleLoginSuccess = async (credentialResponse) => {
    try {
      const id_token = credentialResponse?.credential;
      if (!id_token) return;
      const res = await fetch(`${API_BASE}/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id_token })
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '<no body>');
        console.error('Auth failed', res.status, text);
        return;
      }
      const json = await res.json();
      const u = json.user;
      if (u) {
        // Normalize avatar to absolute
        const absPic = u.picture?.startsWith('/') ? `${API_BASE}${u.picture}` : u.picture;
        const finalUser = { ...u, picture: absPic };
        setUser(finalUser);
        localStorage.setItem('sv_user', JSON.stringify(finalUser));
        // close the auth options after successful login
        setShowAuthOptions(false);
        // let the app know user changed so favorites can refresh
        window.dispatchEvent(new CustomEvent('user-changed'));
      }
    } catch (e) {
      console.error('Login error', e);
    }
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('sv_user');
    setShowAuthOptions(false);
    // notify app
    window.dispatchEvent(new CustomEvent('user-changed'));
  };

  const toggleAuthOptions = (e) => {
    e && e.stopPropagation && e.stopPropagation();
    setShowAuthOptions((s) => !s);
  };

  // We close the auth modal via the overlay click. Keep authRef if further focus handling is needed.

  return (
    <div className={`header-container ${isScrolled ? 'scrolled' : ''}`}>
      <div className="header-inner">
        <div className="brand" onClick={() => navigate('/')}> 
          {/* <img src="/studyvarious-logo.svg" alt="Studyvarious logo" className="header-logo" onError={(e)=>{e.target.style.display='none'}} /> */}
          <h1 className="header-title">Study<strong>various</strong></h1>
        </div>
        <div className="search-box-container">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="What do you want to play?"
            className="search-box"
            aria-label="Search for violin pieces"
          />
          <button
            type="button"
            className="search-button"
            onClick={handleSearch}
            aria-label="Search"
          >
            <FaSearch /> {/* Search icon inside the button */}
          </button>
        </div>
        {/* tuner removed from header (now available in hamburger menu) */}
        {/* Auth area */}
        <div className="auth-area">
          {!user ? (
            <div className="login-wrapper">
              <button className="login-button" onClick={toggleAuthOptions} aria-haspopup="dialog" aria-expanded={showAuthOptions}>Login</button>
            </div>
          ) : (
            <div className="header-user" title={user.name}>
              <img src={user.picture} alt={user.name} className="header-avatar" onClick={toggleAuthOptions} aria-haspopup="dialog" aria-expanded={showAuthOptions} />
            </div>
          )}
        </div>
      </div>

      {/* Auth modal (rendered in portal) */}
      {showAuthOptions && createPortal(
        <div className="auth-modal-overlay" onClick={() => setShowAuthOptions(false)}>
          <div className="auth-modal-box" role="dialog" aria-modal="true" aria-label="Authentication options" onClick={(e) => e.stopPropagation()} ref={authRef}>
            <div className="auth-modal-header">
              <h3>{user ? user.name : 'Sign in'}</h3>
            </div>
            <div className="auth-modal-content">
              {!user ? (
                <>
                  <div className="auth-option-google">
                    <GoogleLogin onSuccess={handleLoginSuccess} onError={() => console.warn('Login Failed')} useOneTap={false} />
                  </div>
                </>
              ) : (
                <div className="user-options">
                  <div className="user-info">
                    <img src={user.picture} alt={user.name} className="modal-avatar" />
                    <div className="user-name">{user.name}</div>
                  </div>
                  <div className="user-actions">
                    <button className="logout-button modal-logout" onClick={() => { handleLogout(); setShowAuthOptions(false); }}>Logout</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default Header;
